import { getEffectiveConfig } from '@marva/shared/product-categories'
import type { Workspace } from '../../stores/workspaceStore'

/**
 * The Store tab's sections (MAR-386) and the pure reads behind them — kept out of the
 * components so each rule has a test that does not need a render.
 *
 * Same `?section=` contract as Limits (`limitsGlance.ts`): the open section is derived from the
 * URL at the read, an unknown or missing value opens the first one, and `screens/Authority.tsx`
 * drops `section` on a tab switch.
 */

export type StoreSectionId = 'type' | 'facts' | 'address' | 'shopify'

export const STORE_SECTIONS: { id: StoreSectionId; label: string }[] = [
  { id: 'type', label: 'Store type' },
  { id: 'facts', label: 'What she may say' },
  { id: 'address', label: 'Postal address' },
  { id: 'shopify', label: 'Shopify connection' },
]

const SECTION_IDS = STORE_SECTIONS.map((s) => s.id) as string[]
const DEFAULT_SECTION: StoreSectionId = 'type'

/** The open section, from the URL alone. */
export const storeSectionFrom = (params: URLSearchParams): StoreSectionId => {
  const raw = params.get('section')
  return raw && SECTION_IDS.includes(raw) ? (raw as StoreSectionId) : DEFAULT_SECTION
}

const FACT_KEYS = ['shipping', 'returns', 'supportContact', 'notes'] as const

/**
 * The sections with nothing set, which the nav marks "Empty". Read from the STORED workspace —
 * never a draft — so the hint is what Marva has, not what is being typed.
 *
 * Store type counts as empty only when the classifier has not run: an inferred type is set,
 * even though the merchant did not set it. Shopify counts as empty when no shop is stored at
 * all (a stored-but-uninstalled shop is a state the card itself reports).
 */
export const emptySections = (workspace: Workspace | null | undefined): Set<StoreSectionId> => {
  const empty = new Set<StoreSectionId>()
  if (!workspace) return empty
  if (!workspace.storeDomain?.archetype) empty.add('type')
  if (!FACT_KEYS.some((k) => (workspace.storeContext?.[k] || '').trim())) empty.add('facts')
  if (!workspace.postalAddress) empty.add('address')
  if (!(workspace.shopify?.shop || workspace.shopify?.storeUrl)) empty.add('shopify')
  return empty
}

/**
 * A money amount in the store's own currency (`workspace.shopify.currency`, the shop's base
 * currency, written at install). No currency known — or one `Intl` refuses — is a plain number:
 * rendering it as £ or $ would invent the very currency we were never told (same rule as the
 * Ledger's `AttributionStrip`).
 */
export const formatMoney = (amount: number, currency: string | null | undefined): string => {
  const whole = Number.isInteger(amount) || amount >= 100
  if (currency) {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        maximumFractionDigits: whole ? 0 : 2,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(amount)
    } catch {
      // An unknown code falls through to a plain number.
    }
  }
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: whole ? 0 : 2, minimumFractionDigits: whole ? 0 : 2 }).format(amount)
}

/** The store-classifier's category keys (`getProductCategory`), in a merchant's words. */
const CATEGORY_LABELS: Record<string, string> = {
  apparel: 'Apparel',
  beauty_skincare: 'Beauty & skincare',
  home_lifestyle: 'Home & lifestyle',
  electronics: 'Electronics',
  food_wellness: 'Food & wellness',
  generic: 'Other',
}

export const categoryLabel = (key: string): string =>
  CATEGORY_LABELS[key] ?? key.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase())

export interface CatalogueView {
  /** Largest share first, as whole percentages. Empty when nothing was measured. */
  categories: { key: string; label: string; percent: number }[]
  avgPrice: string | null
  productCount: string | null
  /** The schema holds booleans only — a yes/no, never a count. */
  sized: 'Yes' | 'No' | null
  consumables: 'Yes' | 'No' | null
  measured: boolean
}

const yesNo = (v: boolean | undefined): 'Yes' | 'No' | null => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : null)

/** What the classifier read in the catalogue, or `measured: false` when it read nothing. */
export const catalogueView = (workspace: Workspace | null | undefined): CatalogueView => {
  const sd = workspace?.storeDomain
  const categories = Object.entries(sd?.categoryDistribution || {})
    .filter(([, pct]) => typeof pct === 'number' && pct > 0)
    .sort(([, a], [, b]) => b - a)
    // The classifier writes fractions; tolerate a row already in percent.
    .map(([key, pct]) => ({ key, label: categoryLabel(key), percent: Math.round(pct <= 1 ? pct * 100 : pct) }))
  const cc = sd?.catalogCharacteristics
  const currency = workspace?.shopify?.currency
  // A zero average is the classifier's "no priced products", not a price of nothing.
  const avgPrice = typeof cc?.avgPrice === 'number' && cc.avgPrice > 0 ? formatMoney(cc.avgPrice, currency) : null
  const productCount = typeof cc?.skuCount === 'number' && cc.skuCount > 0 ? cc.skuCount.toLocaleString('en-GB') : null
  const sized = yesNo(cc?.hasSizedProducts)
  const consumables = yesNo(cc?.hasConsumables)
  return {
    categories,
    avgPrice,
    productCount,
    sized,
    consumables,
    measured: categories.length > 0 || avgPrice !== null || productCount !== null,
  }
}

const SIGNAL_LABELS: Record<string, string> = {
  sizing: 'Sizing',
  stylePreference: 'Style preference',
  reorderCandidates: 'Reorder timing',
  ingredientSensitivity: 'Ingredient sensitivity',
  specComparison: 'Spec comparison',
  projectTracking: 'Project tracking',
}

/**
 * The signals whose ONLY reader is the suitability question packs (`suitability-packs.js`
 * `SIGNAL_PACK_GROUPS`). Every other signal has a reader that runs for every store (sizing,
 * ingredient sensitivity and spec comparison in order-intelligence's return risk and the
 * abandonment intent classifier; reorder timing in both intelligence APIs). These two do
 * nothing unless suitability capture is live, which is dark pending counsel (MAR-56) — so
 * listing them as something she watches for would be a claim about a switched-off feature.
 */
export const SUITABILITY_ONLY_SIGNALS = ['stylePreference', 'projectTracking'] as const

/**
 * Whether suitability capture is live for this workspace — the SAME predicate as
 * `buildSuitabilityConfig` (`@marva/shared/suitability-packs`): `enabled === true` WITH a
 * `disclosureVersion`. Ops-set after counsel; false for every workspace today.
 */
export const suitabilityLive = (workspace: Pick<Workspace, 'suitability'> | null | undefined): boolean =>
  workspace?.suitability?.enabled === true && !!workspace.suitability.disclosureVersion

export interface TypeEffects {
  signals: { key: string; label: string; on: boolean }[]
  emailPerWeek: number
  emailPerMonth: number
  vip: string
  atRiskDays: number
  churnedDays: number
}

/**
 * What the store type changes — read from `getEffectiveConfig(archetype)`, the SAME preset the
 * runtime reads (`resolveEffectiveCaps` seeds a blank email cap from it), never the copy of it
 * stamped on `storeDomain.config` at classification time, which an override does not refresh.
 *
 * ⚠️ No SMS figure. The preset carries `smsPerMonth`, but the enforcer ignores it: a blank SMS
 * cap falls back to the fixed `MAX_SMS_PER_CUSTOMER_MONTH`, whatever the type. Quoting the
 * preset's number would be a cap that is not enforced.
 *
 * ⚠️ Only signals with a live reader. `SUITABILITY_ONLY_SIGNALS` are left out — on or off —
 * unless `suitabilityLive`, since without it nothing reads them.
 */
export const typeEffects = (
  archetype: string | null | undefined,
  currency: string | null | undefined,
  suitabilityOn = false
): TypeEffects => {
  const config = getEffectiveConfig(archetype)
  const { lifecycle, frequency, enabledSignals } = config
  const keys = Object.keys(SIGNAL_LABELS).filter(
    (key) => suitabilityOn || !(SUITABILITY_ONLY_SIGNALS as readonly string[]).includes(key)
  )
  return {
    signals: keys.map((key) => ({ key, label: SIGNAL_LABELS[key], on: enabledSignals[key] === true })),
    emailPerWeek: frequency.emailPerWeek,
    emailPerMonth: frequency.emailPerMonth,
    vip: `${lifecycle.vip.minOrders}+ orders or ${formatMoney(lifecycle.vip.minSpend, currency)}+ spent`,
    atRiskDays: lifecycle.atRiskDays,
    churnedDays: lifecycle.churnedDays,
  }
}

export type EmailCapSource =
  | { kind: 'unknown' }
  | { kind: 'default' }
  | { kind: 'own'; perWeek: number | null; perMonth: number | null }

/**
 * Whether the type's email default is the cap in force, from the stored engagement rules
 * (`GET …/governance/engagement`). Mirrors `resolveEffectiveCaps`: each of week and month is the
 * merchant's number when it is a finite number, else the type's — independently. `unknown` while
 * the read is pending or failed: then the card says neither.
 */
export const emailCapSource = (
  read: { ok: true; perWeek?: unknown; perMonth?: unknown } | { ok: false } | null
): EmailCapSource => {
  if (!read || !read.ok) return { kind: 'unknown' }
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const perWeek = num(read.perWeek)
  const perMonth = num(read.perMonth)
  return perWeek === null && perMonth === null ? { kind: 'default' } : { kind: 'own', perWeek, perMonth }
}

/**
 * What the server keeps of each store fact (`workspaces` PUT: trimmed, then cut to this many
 * characters). The form caps input at the same length, and the mirror applies the same cut, so
 * the screen never shows text the server dropped.
 */
export const FACT_LIMITS = { shipping: 1000, returns: 1000, supportContact: 200, notes: 1000 } as const
